import { useContext, useEffect, useState, useCallback } from 'react';
import {
  setTrk,
  TrkTag,
  TrkElement,
} from '@nerdwallet/nw-client-lib/trk-helper';

import { ImpressionContext } from '@nerdwallet/analytics/react/impression';
import useAnalytics from '../../lib/segment/useAnalytics';
import { useSelector, useDispatch } from 'react-redux';
import { Button } from '@nerdwallet/currency/components/Button';
import { Link } from '@nerdwallet/currency/components/Link';
import { Text } from '@nerdwallet/currency/components/Text';

import { getLogger } from '@nerdwallet/logging';
import { transformTaxonomy } from '../../lib/helpers';
import type { ReduxStore } from '../redux/types';
import { REDIRECT_TO_HOME_TAXES_URLS } from '../lib/constants';
import { SET_GLOBAL_NAV_STATE } from '../redux/actions';
import { getCookie } from '../../../global-nav/components/utilities/cookies';
import type { AuthParams } from '@nerdwallet/nw-auth-links';

import LinkGenerator from '@nerdwallet/nw-auth-links';

const rootLogger = getLogger('TrackedButton');

const buttonTypes = ['signin', 'register'] as const;

const trkCopies = {
  signin: 'log in',
  register: 'join',
};

/* Returns information for tracking parameter trk_copy. */
const getTrkCopy = (type: (typeof buttonTypes)[number]) => {
  return trkCopies[type];
};

/* Adds tracking parameters. */
const getTrkParams = ({ copy = '', trk = {} }) => ({
  [TrkTag.element]: TrkElement.button,
  [TrkTag.copy]: copy.toLowerCase().split(' ').join('_'),
  ...trk,
});

/* Returns a valid button type. */
const validateButtonType = (type: (typeof buttonTypes)[number]) => {
  const logger = getLogger('validateButtonType', rootLogger);
  if (buttonTypes.includes(type)) {
    return type;
  }
  logger.error('Error finding valid button type. Defaulting to register type.');
  return 'register';
};

interface Props {
  buttonName?: string;
  buttonType?: (typeof buttonTypes)[number];
  handleClick?: ({ impressionId }: { impressionId: string }) => void;
  isPrimary?: boolean;
  isTertiary?: boolean;
  styleClasses?: string;
  isLink?: boolean;
  trkLocation?: string;
  trkFormat?: string;
  cohort?: string;
  redirectQueryParam?: string;
  authParamsOverride?: AuthParams;
}

const TrackedButton: React.FC<Props> = ({
  buttonName = 'Register',
  buttonType = 'register',
  handleClick,
  isPrimary = false,
  isTertiary = false,
  styleClasses,
  isLink,
  trkLocation,
  cohort,
  redirectQueryParam = '',
}) => {
  const pageTaxonomy = useSelector<ReduxStore, ReduxStore['pageTaxonomy']>(
    (store) => store.pageTaxonomy
  );

  const authParamsOverride = useSelector<
    ReduxStore,
    ReduxStore['authParamsOverride']
  >((store) => store.authParamsOverride);

  const segmentTrackingAttributes = useSelector<
    ReduxStore,
    ReduxStore['segmentTrackingAttributes']
  >((store) => store.segmentTrackingAttributes);

  const [btnType, setBtnType] = useState(validateButtonType(buttonType));
  // TODO-TS: fix this once ImpressionContext is typed
  const impressionContext = useContext(ImpressionContext) as any;
  const impressionId = impressionContext?.impressionId; // impressionContext may not be defined in test environment

  /* Sets btnType state and revalidates if buttonType prop changes. */
  useEffect(() => {
    const type = validateButtonType(buttonType);
    setBtnType(type);
  }, [buttonType]);

  /* Creates a tracked link for Button using a valid button type. */
  const trkCopy = getTrkCopy(btnType);

  const enrichedSegmentTrackingAttributes = {
    anonymousUserId: segmentTrackingAttributes?.anonymousUserId,
    deployableName: segmentTrackingAttributes?.deployableName,
    deployableVersion: segmentTrackingAttributes?.deployableVersion,
    driverContentId: segmentTrackingAttributes?.driverContentId,
    driverContentSource: segmentTrackingAttributes?.driverContentSource,
    driverElement: 'button',
    driverFlowName: segmentTrackingAttributes?.driverFlowName,
    driverLocation: trkLocation,
    driverPageName: segmentTrackingAttributes?.driverPageName,
    driverPageUrl: window?.location?.href,
    driverPageViewId: segmentTrackingAttributes?.driverPageViewId,
    driverSubLocation: cohort,
    mostRecentTraditionalDeviceId: getCookie('cookie_id'),
  };

  // Get anonymous ID from segment to pass to Auth0.
  // Getting anon ID from segment is async, so this must happen in a useEffect.
  const { getAnonymousId } = useAnalytics();

  const setHeaderPropsDispatch = useDispatch();

  const setHeaderProps = useCallback(
    (props) => {
      setHeaderPropsDispatch({
        ...props,
        type: SET_GLOBAL_NAV_STATE,
      });
    },
    [setHeaderPropsDispatch]
  );

  useEffect(() => {
    async function setAnonymousId() {
      const anonymousUserId = await getAnonymousId();
      if (
        anonymousUserId !== enrichedSegmentTrackingAttributes.anonymousUserId
      ) {
        setHeaderProps({
          segmentTrackingAttributes: { anonymousUserId },
        });
      }
    }
    setAnonymousId();
  }, [enrichedSegmentTrackingAttributes]);

  const trkParams = getTrkParams({
    copy: trkCopy,
  });

  const redirectToTaxHome = REDIRECT_TO_HOME_TAXES_URLS.includes(
    window.location.pathname
  );
  const myNerdWalletRedirectPage = redirectToTaxHome ? 'taxes' : 'dashboard';

  const ntsParams = {
    // Transform feat_bypass_mfa query param to bypassMFA for NTS
    bypassMFA: new URLSearchParams(window.location.search).get(
      'feat_bypass_mfa'
    ),
    redirectTo: `${window?.location?.protocol}//${window?.location?.host}/home/${myNerdWalletRedirectPage}${redirectQueryParam}`,
    initialScreen: '',
  };
  if (btnType === 'register') {
    ntsParams.initialScreen = 'signup';
  }

  const combinedOverrideParams = () => {
    // LinkGenerator expects redirectTo prop, so add it here
    return {
      redirectTo: '',
      ...enrichedSegmentTrackingAttributes,
      ...ntsParams,
      ...authParamsOverride,
    };
  };

  // combine pageTaxonomy and combinedOverrideParams
  // to pass into linkGenerator
  const baseParams = {
    pageTaxonomy,
    ...combinedOverrideParams(),
  };

  const linkGenerator = new LinkGenerator();

  const generatedLink =
    buttonType === 'register'
      ? linkGenerator.register(baseParams)
      : linkGenerator.login(baseParams);

  // Add trk params to the link
  const link = setTrk({
    url: generatedLink.toString(),
    params: trkParams,
    taxonomy: transformTaxonomy(pageTaxonomy),
  });

  /* Opens a signin or register modal. */
  const handleAuthClick = () => {
    // if showAuthDialog is available use that - otherwise let the
    // user follow the link
    if (handleClick) {
      handleClick({ impressionId });
    }
  };

  if (isLink) {
    return (
      <Link
        href={link}
        onClick={handleAuthClick}
        unstyled
        className={styleClasses}
      >
        <Text size="small" tone="light" bold>
          {buttonName}
        </Text>
      </Link>
    );
  }

  return (
    <Button
      href={link}
      onClick={handleAuthClick}
      primary={isPrimary}
      tertiary={isTertiary}
      className={styleClasses}
    >
      {buttonName}
    </Button>
  );
};

export default TrackedButton;
